"use client";

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type CSSProperties,
} from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import {
  integrationsApi,
  lineApi,
  servicesApi,
  transactionsSyncApi,
  type Service,
  type Workspace,
} from "@/lib/api";
import { fetchWorkspaceByIdCached } from "@/lib/workspaceAvatarTags";
import {
  Coins,
  FileSpreadsheet,
  Headset,
  HelpCircle,
  LogIn,
  LogOut,
  Plus,
  User,
} from "lucide-react";
import Logo from "@/app/components/Logo";
import { WorkspaceSidebarIcon } from "@/app/components/WorkspaceSidebarIcon";
import WorkspaceListModal from "@/app/components/WorkspaceListModal";
import { HowAeraWorksSection } from "@/app/components/HowAeraWorksSection";
import Modal from "@/app/components/ui/Modal";
import { AeraImage } from "@/app/components/ui/AeraImage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import { subscribeWorkspaceSelected, emitWorkspaceSelected, readLiveWorkspaceId } from "@/lib/lastWorkspaceStorage";
import {
  buildHomeHrefAfterWorkspacePick,
  continuePendingBillinGo,
} from "@/lib/billinGoRoutes";
import { invalidateAccessibleWorkspaces } from "@/lib/workspaceAccess";
import { navigateHomeAfterWorkspacePick } from "@/lib/workspacePickHint";
import { showWorkspaceSwitchToast } from "@/lib/workspaceSwitchToast";
import { openCookieConsentBanner } from "@/lib/cookieConsent";
import { navigateToSignin } from "@/lib/openLineExternalBrowser";
import { useFinancialWorkDesktop } from "@/lib/financialWork/layout";
import { BillinDataView } from "@/app/artifacts/table/BillinDataView";
import { FinancialWorkShell } from "@/app/financial-work/FinancialWorkShell";
import {
  FinancialWorkPreparingCover,
  ShareConfirmPreparingCover,
  useFinancialWorkPreparing,
} from "@/app/financial-work/FinancialWorkPreparingContext";
import { ShareDisclosureConfirmModal } from "@/app/financial-work/ShareDisclosureConfirmModal";
import { SharedCaseRecipientView } from "@/app/financial-work/SharedCaseRecipientView";
import { fetchReceiptCard } from "@/app/financial-work/ReceiptQueueView";
import { FirstResponder } from "@/app/service/[id]/FirstResponder";
import {
  FINANCIAL_WORK_FILTER_PARAM,
  FINANCIAL_WORK_NAV_PARAM,
} from "@/lib/financialWork/nav";
import {
  EXPENSE_CONTEXT_IDS_PARAM,
  caseReviewRequested,
  expenseCaseIdFromSearch,
  expenseContextIdsFromSearch,
  RECEIPT_OBJECT_KIND,
  shareConfirmRequested,
  type ReceiptQueueCard,
} from "@/lib/financialWork/receiptQueue";
import {
  sharedCaseEntryRequested,
  sharedCaseTokenFromSearch,
} from "@/lib/financialWork/recipientEntry";
import { parseRevisionFromSearch } from "@/lib/financialWork/lineDeepLink";
import { useOrdersContext } from "@/context/OrdersContext";
import {
  BILLIN_RECEIVABLE_WORKER_ID,
  BILLIN_SERVICE_ID,
} from "@/lib/billinConstants";
import {
  BILLIN_SCROLL_TO_GOOGLE_SHEETS,
  BILLIN_SCROLL_TO_RECEIVABLE,
  BILLIN_SCROLL_TO_TRANSACTIONS,
  type BillinScrollTarget,
} from "@/lib/billinGoRoutes";
import {
  getGoogleConnectionPresentation,
  getGoogleSheetUrl,
  maskEmailForDisplay,
  parseGoogleIntegrationStatus,
  type GoogleIntegrationStatus,
  type GoogleSheetSyncHealth,
} from "@/lib/googleIntegrationStatus";
import { navigateToGoogleIntegrationsConnect } from "@/lib/googleIntegrationsConnect";
import { type Transaction } from "@aera/data-table";
import { openTransactionsPrintView as openTransactionsPrintViewShared } from "@/lib/openTransactionsPrintView";
import { useWorkerLayout } from "@/hooks/useWorkerLayout";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { LineLogo } from "@/app/components/LineLogo";
import { ConnectionLinkButton } from "@/app/components/billin/ConnectionLinkButton";
import {
  GoogleConnectionStatusBadge,
  LineBindStatusBadge,
} from "@/app/components/billin/ConnectionStatusBadge";
import { IntegrationConnectionCard } from "@/app/components/billin/IntegrationConnectionCard";
import { ProfileMagicServiceBanner } from "@/app/components/profile-magic/ProfileMagicServiceBanner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/components/ui/Tooltip";
import { DEFAULT_BILLIN_LINE_DEEP_LINK } from "@/hooks/useLineBillinDeepLink";

const PENDING_GOOGLE_SHEET_LINK_HINT =
  "เชื่อมต่อบัญชี Google แล้ว แต่ยังไม่มีโฟลเดอร์/ชีต AERA — กด「สร้างชีต」หรือส่งสลิปให้บิลลิน เพื่อสร้างสำเนา";

const WORKSPACE_ITEM_SIZE_PX = 40;
const WORKSPACE_ITEM_GAP_PX = 8;
const WORKSPACE_TOP_INDICATOR_CLASS =
  "h-1 w-10 rounded-b-lg bg-aera-charcoal";

/** Error codes set by market-api's OAuth callback (`googleConnectError` param). */
function googleConnectErrorMessage(code: string): string {
  switch (code) {
    case "access_denied":
      return "การเชื่อมต่อถูกยกเลิก เพราะไม่ได้กดยินยอมที่หน้า Google — กดเชื่อมต่อแล้วลองใหม่ได้เลย";
    case "state_expired":
    case "invalid_state":
      return "ลิงก์เชื่อมต่อหมดอายุ กรุณากดเชื่อมต่อใหม่อีกครั้ง";
    case "no_refresh_token":
      return "Google ไม่ได้ส่งสิทธิ์การเข้าถึงแบบต่อเนื่องกลับมา กรุณาเชื่อมต่อใหม่และกดยินยอมทุกรายการ";
    default:
      return `เชื่อมต่อ Google ไม่สำเร็จ (${code}) กรุณาลองใหม่อีกครั้ง`;
  }
}

function unwrapWorkspace(data: unknown): Workspace | null {
  if (!data || typeof data !== "object") return null;
  const maybe = data as { data?: unknown; workspace?: unknown };
  const inner = maybe.data ?? maybe.workspace ?? data;
  if (!inner || typeof inner !== "object") return null;
  return inner as Workspace;
}

function formatAgentDashboardLabel(title: string | null | undefined): string {
  const t = (title ?? "").trim();
  if (!t) return "แดชบอร์ด";
  return `${t} · แดชบอร์ด`;
}

function openTransactionsPrintView(params: {
  actionLabel: string;
  schemaLabel?: string;
  rows: Transaction[];
  warning?: string;
  targetWindow?: Window | null;
}) {
  return openTransactionsPrintViewShared(params);
  if (typeof window === "undefined") return;
  const { actionLabel, rows, schemaLabel, warning } = params;

  const FIELD_LABELS_TH: Record<string, string> = {
    id: "ไอดี (ระบบ)",
    created_at: "สร้างเมื่อ",
    recorded_at: "บันทึกเมื่อ",
    record_date: "วันที่รายการ",
    record_id: "รหัสรายการ",
    job_type: "ประเภท",
    amount: "จำนวนเงิน",
    payment_channel: "ช่องทางชำระเงิน",
    vendor: "ร้านค้า",
    receipt_date: "วันที่ในใบเสร็จ",
    category: "หมวดหมู่",
    tax_invoice: "ใบกำกับภาษี",
    seller_tax_id: "เลขผู้เสียภาษีผู้ขาย",
    vat: "ภาษีมูลค่าเพิ่ม (VAT)",
    status: "สถานะ",
    remark: "หมายเหตุ",
    image_url: "รูป/ไฟล์แนบ",
    workspace_id: "Workspace (ระบบ)",
    owner_user_id: "เจ้าของ (ระบบ)",
    user_id: "ผู้ใช้ (ระบบ)",
  };
  const fieldLabel = (key: string) =>
    FIELD_LABELS_TH[key] ?? key.replaceAll("_", " ");

  const safe = (v: unknown) =>
    String(v ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");

  const safeValue = (v: unknown) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return safe(v);
    if (typeof v === "number" || typeof v === "boolean") return safe(v);
    try {
      return safe(JSON.stringify(v, null, 2));
    } catch {
      return safe(String(v));
    }
  };

  const now = new Date();
  const ORDER: string[] = [
    "record_date",
    "record_id",
    "job_type",
    "amount",
    "payment_channel",
    "vendor",
    "receipt_date",
    "category",
    "tax_invoice",
    "seller_tax_id",
    "vat",
    "status",
    "remark",
    "image_url",
    "recorded_at",
    "created_at",
    "id",
  ];

  const columnKeys = Array.from(
    new Set(
      rows.flatMap((r) =>
        Object.keys(r).filter(
          (k) => !["workspace_id", "owner_user_id", "user_id"].includes(k),
        ),
      ),
    ),
  ).sort((a, b) => {
    const ia = ORDER.indexOf(a);
    const ib = ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  const headerCells = columnKeys
    .map((k) => `<th title="${safe(k)}">${safe(fieldLabel(k))}</th>`)
    .join("");

  const bodyRows = rows
    .map((r) => {
      const rec = r as Record<string, unknown>;
      const tds = columnKeys
        .map((k) => `<td>${safeValue(rec[k])}</td>`)
        .join("");
      return `<tr>${tds}</tr>`;
    })
    .join("");

  const recordCount = rows.length;
  const warningHtml = warning
    ? `<div class="warn" role="note">${safe(warning)}</div>`
    : "";

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safe(actionLabel)}${schemaLabel ? ` — ${safe(schemaLabel)}` : ""}</title>
    <style>
      :root {
        color-scheme: light;
        --ink: #0f172a;
        --muted: #475569;
        --line: #fed7aa; /* orange-200 */
        --soft: #fff7ed; /* orange-50 */
        --card: #ffffff;
        --accent: #f59e0b; /* amber-500 */
        --accentSoft: #ffedd5; /* orange-100 */
        --accentInk: #9a3412; /* orange-800 */
        --shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
      }
      * { box-sizing: border-box; }
      html, body { height: 100%; }
      body {
        font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Noto Sans Thai", "Noto Sans", sans-serif;
        margin: 0;
        color: var(--ink);
        background: radial-gradient(1200px 600px at 10% -10%, #ffedd5, transparent 60%),
                    radial-gradient(900px 500px at 95% 0%, #fde68a, transparent 55%),
                    #fff7ed;
      }
      .page {
        max-width: 860px;
        margin: 22px auto;
        padding: 0 18px 28px;
      }
      .sheet {
        background: var(--card);
        border: 1px solid var(--line);
        border-radius: 16px;
        box-shadow: var(--shadow);
        overflow: hidden;
      }
      header {
        padding: 18px 18px 14px;
        border-bottom: 1px solid var(--line);
        background: linear-gradient(180deg, #ffffff, #fff7ed);
      }
      .titleRow {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
      }
      .brand {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
      }
      .logo {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
      }
      .brandText { min-width: 0; }
      h1 {
        font-size: 18px;
        line-height: 1.2;
        margin: 0;
        letter-spacing: -0.01em;
      }
      .pill {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        border: 1px solid var(--line);
        border-radius: 999px;
        font-size: 12px;
        color: var(--muted);
        background: var(--soft);
        white-space: nowrap;
      }
      .pill b { color: var(--ink); font-weight: 700; }
      .meta {
        margin-top: 10px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px 12px;
        font-size: 12px;
        color: var(--muted);
      }
      .meta div { padding: 8px 10px; border: 1px dashed var(--line); border-radius: 12px; background: #fff; }
      .meta .k { font-size: 11px; color: #64748b; display: block; margin-bottom: 2px; }
      .meta .v { font-weight: 700; color: var(--ink); }
      .content { padding: 16px 18px 18px; }
      .tableWrap {
        border: 1px solid var(--line);
        border-radius: 14px;
        overflow: hidden;
        background: #fff;
      }
      .tableScroll { overflow: auto; }
      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        table-layout: fixed;
      }
      th, td {
        padding: 10px 12px;
        border-bottom: 1px solid #eef2f7;
        vertical-align: top;
        font-size: 12px;
      }
      th {
        position: sticky;
        top: 0;
        background: linear-gradient(180deg, #ffffff, var(--soft));
        color: #334155;
        font-weight: 800;
        text-align: left;
        border-bottom: 1px solid #e6edf6;
        white-space: normal;
        z-index: 1;
      }
      tr:last-child td { border-bottom: none; }
      td { color: var(--ink); white-space: pre-wrap; word-break: break-word; }
      th, td { border-right: 1px solid #eef2f7; }
      th:last-child, td:last-child { border-right: none; }
      tbody tr:nth-child(odd) td { background: #ffffff; }
      tbody tr:nth-child(even) td { background: #fbfdff; }
      footer {
        padding: 12px 18px;
        border-top: 1px solid var(--line);
        font-size: 11px;
        color: #64748b;
        background: #ffffff;
        display: flex;
        justify-content: space-between;
        gap: 10px;
      }
      .hint { color: #64748b; }
      .warn {
        margin: 12px 18px 0;
        padding: 10px 12px;
        border: 1px solid #fdba74; /* orange-300 */
        background: #fff7ed; /* orange-50 */
        color: var(--accentInk);
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
      }
      .printBtn {
        appearance: none;
        border: 1px solid var(--line);
        background: var(--accentSoft);
        color: var(--accentInk);
        border-radius: 10px;
        padding: 8px 10px;
        font-weight: 700;
        font-size: 12px;
        cursor: pointer;
      }
      .printBtn:hover { background: #fed7aa; }
      @media (max-width: 640px) {
        .meta { grid-template-columns: 1fr; }
        .pill { white-space: normal; }
      }
      @media print {
        body { background: #fff; }
        .page { margin: 0; padding: 0; max-width: none; }
        .sheet { border: none; border-radius: 0; box-shadow: none; }
        header, .content, footer { padding-left: 14mm; padding-right: 14mm; }
        .tableScroll { overflow: visible; }
        th { position: static; }
        .printBtn { display: none; }
        .warn { display: none; }
      }
    </style>
  </head>
  <body>
    <div class="page">
      <div class="sheet">
        <header>
          <div class="titleRow">
            <div class="brand">
              <div class="logo" aria-label="HireAera">
                <svg width="28" height="28" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M50.2762 14.9033C62.4673 9.20388 75.6794 9.59616 84.1795 17.0703C96.3054 27.7328 94.0434 54.9322 79.3827 70.2198C65.3555 84.8466 45.7805 84.2641 33.494 74.8477C28.8431 79.7515 23.5557 83.2788 20.0077 83.7276C18.7613 84.1766 17.6112 84.4241 16.5985 84.4395C14.7887 84.8699 13.1802 84.9174 11.8583 84.5235C10.6291 84.4202 9.55634 84.0617 8.67759 83.4209C8.14033 83.1697 7.65141 82.8454 7.21568 82.4453C6.84167 82.1019 6.51498 81.7096 6.23521 81.2735C4.69092 79.6605 3.99702 77.1998 4.05552 74.167C3.27418 64.6517 10.7921 48.4116 23.2665 34.8272C32.2496 25.0446 42.1135 18.0004 50.2762 14.9033Z" fill="#0A0A0A"/>
                  <path d="M50.2904 0.146701C50.672 0.370426 50.9353 0.593176 51.1121 1.00758C51.2005 1.36372 51.2181 1.69189 51.2141 2.05809C51.2129 2.17215 51.2121 2.2862 51.2113 2.40371C51.2097 2.5266 51.2085 2.6495 51.2069 2.77612C51.2061 2.90541 51.2053 3.0347 51.2045 3.16398C51.2013 3.71048 51.1972 4.25696 51.1912 4.80344C51.1748 6.31584 51.1717 7.79272 51.4941 9.27912C51.6981 9.30048 51.6981 9.30048 51.8477 9.13132C53.5353 7.27868 54.7777 5.08008 56.0053 2.9121C56.3165 2.36327 56.6324 1.81773 56.9608 1.27906C57.0216 1.17944 57.0825 1.07983 57.1449 0.977192C57.4525 0.51896 57.8201 0.199144 58.3657 0.0568247C58.9253 0.0118676 59.2969 0.0599499 59.7973 0.342051C60.1241 0.657552 60.3576 0.946364 60.3936 1.4057C60.346 2.13325 59.9109 2.77341 59.5649 3.40228C59.5145 3.49394 59.4641 3.58558 59.4125 3.68001C59.0733 4.29348 58.7193 4.89588 58.3537 5.49396C57.4665 6.95236 56.6865 8.40812 56.1705 10.0397C56.1493 10.1071 56.1277 10.1744 56.1057 10.2438C55.7377 11.4474 55.4449 13.0479 55.9977 14.229C56.4753 14.973 57.1825 15.2362 58.0165 15.443C58.9849 15.64 59.9165 15.6899 60.9033 15.6882C61.1737 15.6878 61.4437 15.6891 61.7137 15.6909C62.3833 15.695 63.0524 15.696 63.722 15.6972C64.2912 15.6983 64.8609 15.7006 65.4301 15.7043C65.6953 15.7056 65.9605 15.7052 66.2257 15.7048C66.3885 15.7056 66.5513 15.7066 66.7141 15.7076C66.8233 15.7068 66.8233 15.7068 66.9349 15.7059C67.5137 15.7125 67.9625 15.8344 68.4169 16.2137C68.8517 16.7432 68.8257 17.2691 68.7689 17.931C68.6141 18.3847 68.4069 18.6211 68.0053 18.8817C67.5041 19.0946 67.0525 19.0978 66.5177 19.0935C66.3821 19.0935 66.382 19.0935 66.2436 19.0936C65.9464 19.0934 65.6489 19.0919 65.3517 19.0904C65.1449 19.0901 64.9381 19.0898 64.7313 19.0896C64.1881 19.0889 63.6449 19.087 63.1017 19.0849C62.5469 19.083 61.9925 19.0821 61.4377 19.0812C60.35 19.0791 59.2625 19.0759 58.1749 19.0719C59.2317 20.1318 60.4789 20.9425 61.7669 21.6965C62.0013 21.8343 62.2349 21.974 62.4681 22.1143C63.0965 22.4919 63.7253 22.8684 64.3593 23.2366C64.4317 23.2789 64.5045 23.3212 64.5793 23.3647C64.7145 23.4431 64.8497 23.5212 64.9853 23.5989C66.3165 24.3705 66.3165 24.3705 66.5737 25.0616C66.7285 25.6515 66.6881 26.1452 66.3829 26.6779C66.0901 27.0202 65.8877 27.2192 65.4281 27.2691C64.8993 27.283 64.5221 27.187 64.0625 26.9215C63.9957 26.8849 63.9293 26.8482 63.8605 26.8105C62.9833 26.3195 62.1285 25.7873 61.2697 25.2645C58.9385 23.8452 55.9213 22.058 53.0629 22.56C52.5261 22.6971 52.0717 22.8956 51.7357 23.3492C50.9401 24.7867 50.9109 26.4824 50.8945 28.0784C50.8929 28.2118 50.8909 28.3452 50.8893 28.4786C50.8837 28.8985 50.8784 29.3183 50.8736 29.7381C50.868 30.224 50.8621 30.7099 50.8553 31.1957C50.8529 31.3872 50.8509 31.5786 50.8489 31.77C50.8405 32.4051 50.8245 33.0398 50.7945 33.6742C50.7893 33.7956 50.7837 33.9169 50.7785 34.042C50.7101 34.5232 50.5637 34.8393 50.2057 35.1693C49.6929 35.5141 49.1328 35.478 48.5352 35.4248C48.1216 35.3101 47.9053 35.0996 47.6609 34.746C47.3637 34.1207 47.4369 33.4136 47.4449 32.7374C47.4453 32.5854 47.4457 32.4333 47.4457 32.2812C47.4465 31.9624 47.4485 31.6438 47.4513 31.3251C47.4553 30.9224 47.4568 30.5196 47.4576 30.1169C47.4616 28.5251 47.4721 26.9356 47.5809 25.3468C47.2605 25.605 46.9653 25.844 46.7145 26.1721C46.6653 26.236 46.6161 26.3 46.5653 26.3659C46.5149 26.4336 46.4641 26.5012 46.4117 26.5709C46.3577 26.6428 46.3032 26.7146 46.2472 26.7886C44.6868 28.8709 43.4241 31.1528 42.1377 33.4085C41.7497 34.0879 41.3568 34.7643 40.9636 35.4407C40.6432 35.9929 40.3253 36.5465 40.0105 37.102C39.9071 37.2841 39.8036 37.4661 39.7001 37.6482C39.634 37.7648 39.5681 37.8816 39.5024 37.9985C38.5712 39.6554 38.5713 39.6554 37.7684 39.9587C37.0826 40.0564 36.6102 39.9966 36.0326 39.6081C35.6909 39.2458 35.5916 38.85 35.6019 38.3532C35.7153 37.7356 36.0983 37.1959 36.4143 36.6608C36.5137 36.49 36.613 36.3192 36.7122 36.1483C36.937 35.7622 37.1631 35.377 37.39 34.9922C37.752 34.3776 38.1095 33.7606 38.4663 33.143C38.8736 32.4384 39.2825 31.7347 39.6949 31.033C42.3057 26.8767 42.3057 26.8767 43.3161 22.1559C43.3153 22.086 43.3149 22.0162 43.3145 21.9442C43.2993 21.2342 43.1837 20.593 42.6765 20.0549C41.1445 18.7873 38.6954 19.1027 36.8356 19.0711C36.5766 19.0666 36.3176 19.0611 36.0586 19.0546C35.8167 19.0487 35.5748 19.0442 35.3329 19.0416C34.6672 19.0243 34.2093 18.9613 33.6465 18.5965C33.3007 18.1831 33.1446 17.7084 33.1693 17.1704C33.2835 16.5799 33.5358 16.268 34.0283 15.9344C34.3942 15.7584 34.6868 15.7201 35.0916 15.7184C35.208 15.7175 35.3244 15.7165 35.4443 15.7155C35.5698 15.7153 35.6953 15.7151 35.8245 15.7149C35.9565 15.7138 36.0884 15.7127 36.2203 15.7114C36.778 15.7062 37.3357 15.7032 37.8934 15.7008C38.3014 15.699 38.7093 15.6958 39.1172 15.6911C39.1831 15.6904 39.2489 15.6896 39.3168 15.6889C40.3733 15.6746 41.3929 15.5824 42.4273 15.364C42.4273 15.0452 42.168 14.8876 41.952 14.6693C41.8844 14.6024 41.8168 14.5356 41.7472 14.4667C41.6776 14.3981 41.6085 14.3295 41.5373 14.2588C40.9165 13.6532 40.2629 13.1188 39.5639 12.6068C39.4639 12.5314 39.3638 12.456 39.2608 12.3784C38.3322 11.6886 37.3376 11.1048 36.3451 10.5122C36.0162 10.3151 35.6883 10.1164 35.3607 9.91716C34.3212 9.28512 33.2805 8.65628 32.2238 8.05296C31.488 7.63188 30.7611 7.1962 30.0349 6.75904C29.5821 6.4868 29.129 6.21656 28.6683 5.9578C28.5945 5.916 28.5207 5.87424 28.4447 5.8312C28.3118 5.75636 28.1784 5.68252 28.0442 5.60996C27.5442 5.3272 27.1305 4.94872 26.8702 4.43028C26.7556 3.17307 26.7557 3.17307 27.0671 2.7011C27.3395 2.40007 27.6211 2.25504 28.0155 2.14848C29.3227 2.10358 30.7443 3.33657 31.816 3.98128C32.301 4.27288 32.7878 4.56112 33.2767 4.84624C33.7575 5.12688 34.2369 5.40964 34.7143 5.69596C35.5721 6.21012 36.4337 6.71784 37.2966 7.22348C37.4675 7.32368 37.6384 7.42392 37.8092 7.52432C38.3278 7.82876 38.8484 8.1294 39.373 8.42344C39.4309 8.456 39.4888 8.4886 39.5485 8.52216C41.2925 9.48828 43.8193 10.4635 45.8153 9.91492C46.1385 9.81348 46.3577 9.67196 46.6265 9.46928C46.6897 9.46928 46.7525 9.46928 46.8173 9.46928C46.8589 9.38508 46.8588 9.38508 46.9012 9.29916C46.9928 9.11916 47.0925 8.94312 47.1933 8.76808C47.9941 7.15784 47.7925 5.06888 47.8173 3.31758C47.8213 3.06169 47.8261 2.80583 47.8309 2.54995C47.8317 2.47308 47.8325 2.39622 47.8337 2.31702C47.8469 1.6918 47.8873 1.02444 48.3325 0.544084C48.3797 0.491384 48.4264 0.438684 48.4748 0.384389C48.9736 -0.0554313 49.6812 -0.0933181 50.2904 0.146701Z" fill="#F59E0B"/>
                </svg>
              </div>
              <div class="brandText">
                <h1>${safe(actionLabel)}</h1>
              </div>
            </div>
            <div class="pill">จำนวนรายการ <b>${safe(recordCount.toLocaleString("th-TH"))}</b></div>
          </div>
          <div class="meta">
            <div><span class="k">พิมพ์เมื่อ</span><span class="v">${safe(now.toLocaleString("th-TH"))}</span></div>
            <div><span class="k">ชุดข้อมูล</span><span class="v">${safe(schemaLabel ?? "-")}</span></div>
          </div>
        </header>
        ${warningHtml}
        <div class="content">
          <div class="tableWrap">
            <div class="tableScroll" aria-label="รายการธุรกรรม">
              <table>
                <thead><tr>${headerCells}</tr></thead>
                <tbody>${bodyRows || `<tr><td colspan="${columnKeys.length}" style="padding:16px;color:#64748b;font-weight:600;">ไม่มีข้อมูลที่จะแสดง</td></tr>`}</tbody>
              </table>
            </div>
          </div>
        </div>
        <footer>
          <div class="hint">เคล็ดลับ: กด “พิมพ์/บันทึก PDF” แล้วเลือก “Save as PDF”</div>
          <div style="display:flex; gap:10px; align-items:center;">
            <button class="printBtn" type="button" onclick="window.print()">พิมพ์/บันทึก PDF</button>
            <div>Generated by HireAera</div>
          </div>
        </footer>
      </div>
    </div>
    <script>window.focus();</script>
  </body>
</html>`;

  // Prefer Blob URL over document.write (more reliable vs. popup/CSP quirks).
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  // Force target="_blank" via anchor click (more reliable than window.open in some browsers).
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Cleanup after a delay (avoid revoking before the tab fully loads).
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

void openTransactionsPrintView;

// ─── Types ────────────────────────────────────────────────────────────────────

interface LineBindStatus {
  bound: boolean;
  lineUserId?: string;
  code?: string;
  deepLink?: string;
  oaInfo?: { name?: string; pictureUrl?: string };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ConnectSection({
  status,
  workspaceId,
  loading,
  onConnect,
  connecting,
  onProvision,
  provisioning,
  onBackfillSheet,
  backfilling,
  sheetSyncHealth,
}: {
  status: GoogleIntegrationStatus | null;
  workspaceId?: string | null;
  loading: boolean;
  onConnect: () => void;
  connecting: boolean;
  onProvision?: () => void;
  provisioning?: boolean;
  onBackfillSheet?: () => void;
  backfilling?: boolean;
  sheetSyncHealth?: GoogleSheetSyncHealth;
}) {
  const [pendingHintOpen, setPendingHintOpen] = useState(false);
  const sheetUrl = getGoogleSheetUrl(status, workspaceId);
  const hasSheetLink = Boolean(sheetUrl);
  const { tone: connectionTone } =
    getGoogleConnectionPresentation(status, workspaceId, { sheetSyncHealth });
  const isConnectedNoLink = connectionTone === "pending-link";
  const isDisconnected = connectionTone === "disconnected";
  const needsReconnect = connectionTone === "needs-reconnect";
  // sync-failed keeps connected actions (open sheet) — only badge changes.

  useEffect(() => {
    if (!isConnectedNoLink) {
      setPendingHintOpen(false);
    }
  }, [isConnectedNoLink]);

  const handleSheetAction = () => {
    if (hasSheetLink && sheetUrl) {
      window.open(sheetUrl, "_blank", "noopener,noreferrer");
      // Optional catch-up refresh — mutations already enqueue Live Mirror themselves.
      onBackfillSheet?.();
    }
  };

  const handlePendingHintOpenChange = (open: boolean) => {
    if (window.matchMedia("(hover: hover)").matches) {
      setPendingHintOpen(open);
    }
  };

  const handlePendingHintClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (!isConnectedNoLink) return;
    event.preventDefault();
    setPendingHintOpen((open) => !open);
  };

  const sheetActionButton = (
    <ConnectionLinkButton
      onClick={hasSheetLink ? handleSheetAction : handlePendingHintClick}
      disabled={!hasSheetLink && !isConnectedNoLink}
      ariaLabel={
        hasSheetLink
          ? "เปิดสำเนา Google"
          : isConnectedNoLink
            ? "ดูวิธีรับลิงก์สำเนา Google"
            : "สำเนา Google"
      }
      icon={hasSheetLink ? "external" : "alert"}
      ariaExpanded={isConnectedNoLink ? pendingHintOpen : undefined}
    />
  );

  const sheetAction = isConnectedNoLink ? (
    <TooltipProvider>
      <Tooltip
        delayDuration={0}
        open={pendingHintOpen}
        onOpenChange={handlePendingHintOpenChange}
      >
        <TooltipTrigger asChild>{sheetActionButton}</TooltipTrigger>
        <TooltipContent
          side="top"
          align="end"
          sideOffset={8}
          collisionPadding={16}
          className="w-max max-w-[min(17rem,calc(100vw-2rem))] whitespace-normal wrap-break-word bg-aera-white text-left leading-relaxed"
        >
          <p>{PENDING_GOOGLE_SHEET_LINK_HINT}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    sheetActionButton
  );

  return (
      <IntegrationConnectionCard
        icon={
          <FileSpreadsheet
            className="h-5 w-5 text-aera-green"
            strokeWidth={2}
            aria-hidden
          />
        }
        title="สำเนา Google"
        subtitle={
          status?.email
            ? maskEmailForDisplay(status.email)
            : isDisconnected
              ? "ไม่บังคับ — สำเนา Sheet/Drive เป็นทางเลือก"
              : "Google เป็นสำเนาเปิดดู"
        }
        subtitleTitle={status?.email}
        loading={loading || Boolean(provisioning)}
        loadingLabel={provisioning ? "กำลังสร้างชีต" : undefined}
        showActions={status !== null}
        actions={
          isDisconnected || needsReconnect ? (
            <AeraButton
              variant="primary"
              size="xs"
              fontWeight="medium"
              onClick={onConnect}
              disabled={connecting}
              loading={connecting}
            >
              {connecting
                ? "กำลังเชื่อมต่อ..."
                : needsReconnect
                  ? "เชื่อมต่อสำเนาใหม่"
                  : "เชื่อมต่อสำเนา"}
            </AeraButton>
          ) : (
            <>
              <GoogleConnectionStatusBadge
                status={status}
                workspaceId={workspaceId}
                sheetSyncHealth={sheetSyncHealth}
              />
              {isConnectedNoLink && onProvision && !provisioning ? (
                <AeraButton
                  variant="secondary"
                  size="xs"
                  fontWeight="medium"
                  onClick={onProvision}
                  disabled={connecting || backfilling}
                >
                  สร้างชีต
                </AeraButton>
              ) : null}
              {sheetAction}
            </>
          )
        }
      />
  );
}

// ─── LINE Bind Section ────────────────────────────────────────────────────────

function LineBindSection({ serviceId }: { serviceId: string }) {
  const [bindStatus, setBindStatus] = useState<LineBindStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBindStatus = useCallback(async () => {
    try {
      const res = (await lineApi.getBindStatus(serviceId)) as LineBindStatus;
      setBindStatus(res);
      if (res.bound && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => {
    fetchBindStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchBindStatus]);

  // Auto-poll ทุก 4 วินาที เมื่อยังไม่ bind
  useEffect(() => {
    if (bindStatus !== null && !bindStatus.bound) {
      pollRef.current = setInterval(fetchBindStatus, 4000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bindStatus?.bound, fetchBindStatus]);

  return (
    <IntegrationConnectionCard
      icon={<LineLogo className="h-5 w-5" />}
      title="LINE OA"
      subtitle={
        bindStatus?.bound && bindStatus.oaInfo?.name
          ? bindStatus.oaInfo.name
          : undefined
      }
      loading={loading}
      showActions={bindStatus !== null}
      actions={
        bindStatus?.bound ? (
          <>
            <LineBindStatusBadge bound />
            <ConnectionLinkButton
              href={DEFAULT_BILLIN_LINE_DEEP_LINK}
              ariaLabel="เปิด LINE @HireAera"
            />
          </>
        ) : (
          <AeraButton
            as="a"
            href={bindStatus?.deepLink?.trim() || DEFAULT_BILLIN_LINE_DEEP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            variant="primary"
            size="xs"
            fontWeight="medium"
          >
            เชื่อมต่อ
          </AeraButton>
        )
      }
    />
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function FinancialAdmin({
  serviceId,
  containerWidth,
}: {
  serviceId: string;
  containerWidth?: number;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const transactionsSectionRef = useRef<HTMLDivElement>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const [isContentScrolled, setIsContentScrolled] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [transactionsSectionEl, setTransactionsSectionEl] =
    useState<HTMLDivElement | null>(null);
  const [googleSheetsSectionEl, setGoogleSheetsSectionEl] =
    useState<HTMLDivElement | null>(null);
  const [isDirectPage, setIsDirectPage] = useState(false);
  const [status, setStatus] = useState<GoogleIntegrationStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const autoProvisionAttemptedRef = useRef(false);
  const [backWorkspaceId, setBackWorkspaceId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const live = new URLSearchParams(window.location.search);
    return (
      live.get("workspaceId")?.trim() ||
      live.get("workspaceid")?.trim() ||
      null
    );
  });
  const queryWorkspaceSeenRef = useRef<string | null>(null);
  const workspacePickLockRef = useRef<{ id: string; at: number } | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [serviceTitle, setServiceTitle] = useState<string | null>(null);
  const [sheetSyncHealth, setSheetSyncHealth] =
    useState<GoogleSheetSyncHealth>(null);

  const {
    isLoggedIn,
    isLoadingSession,
    workspaces,
    isLoadingWorkspaces,
    userAvatar,
    userDisplayName,
    fetchWorkspaces,
    isWorkspaceModalOpen,
    setIsWorkspaceModalOpen,
    handleLogoutAction,
  } = useWorkerLayout();
  const [isLoginNavigationPending, setIsLoginNavigationPending] = useState(false);
  const isLoginButtonLoading = isLoadingSession || isLoginNavigationPending;
  const isLoginButtonDisabled = isLoginButtonLoading;

  useEffect(() => {
    return subscribeWorkspaceSelected((workspaceId) => {
      workspacePickLockRef.current = { id: workspaceId, at: Date.now() };
      queryWorkspaceSeenRef.current = workspaceId;
      setBackWorkspaceId(workspaceId);
    });
  }, []);
  const { orders } = useOrdersContext();
  const isDesktop = useFinancialWorkDesktop();

  const queryWorkspaceId =
    searchParams?.get("workspaceId")?.trim() ||
    searchParams?.get("workspaceid")?.trim() ||
    null;
  /** Prefer the shop the user just picked — URL search params can still be the previous shop. */
  const effectiveWorkspaceId = backWorkspaceId ?? queryWorkspaceId;
  const displayWorkspaceName =
    workspaceName ??
    (effectiveWorkspaceId
      ? (workspaces.find((w) => w.id === effectiveWorkspaceId)?.name ?? null)
      : null);
  const preparing = useFinancialWorkPreparing();
  const hired = useMemo(
    () =>
      orders.some(
        (order) =>
          order.serviceId === serviceId &&
          (!effectiveWorkspaceId ||
            (order.workspaceId || order.workspace?.id) === effectiveWorkspaceId),
      ),
    [effectiveWorkspaceId, orders, serviceId],
  );
  const hireSource = searchParams?.get("source");
  // Members open a hired shop via source=order without an order of their own.
  // The scroll box must follow that work screen, not only the buyer's orders.
  const workChrome =
    hired || hireSource === "order" || hireSource === "my-team";

  const liveSearchParams = useMemo(() => {
    if (typeof window !== "undefined") {
      return new URLSearchParams(window.location.search);
    }
    return searchParams ?? new URLSearchParams();
  }, [searchParams]);
  const shareConfirmDeepLink = shareConfirmRequested(liveSearchParams);
  const shareConfirmCaptureIds = useMemo(
    () => expenseContextIdsFromSearch(liveSearchParams),
    [liveSearchParams],
  );
  const caseReviewDeepLink = caseReviewRequested(liveSearchParams);
  const caseReviewCaseId = useMemo(
    () => expenseCaseIdFromSearch(liveSearchParams),
    [liveSearchParams],
  );
  const sharedCaseEntry = sharedCaseEntryRequested(liveSearchParams);
  const sharedCaseToken = useMemo(
    () => sharedCaseTokenFromSearch(liveSearchParams),
    [liveSearchParams],
  );
  const sharedCaseLinkRevision = useMemo(
    () => parseRevisionFromSearch(liveSearchParams),
    [liveSearchParams],
  );
  const [shareDeepLinkCards, setShareDeepLinkCards] = useState<
    ReceiptQueueCard[]
  >([]);
  const shareConfirmModalOpen =
    shareConfirmDeepLink &&
    shareConfirmCaptureIds.length > 0 &&
    Boolean(effectiveWorkspaceId);
  const [sharePortalReady, setSharePortalReady] = useState(false);

  // Rewrite legacy fw=sources → ใบเสร็จ / Bill-Lin กำลังถือ (do not lift cover yet).
  useEffect(() => {
    if (!shareConfirmDeepLink) return;
    const params = new URLSearchParams(
      typeof window !== "undefined"
        ? window.location.search
        : searchParams?.toString() || "",
    );
    const liveFw = params.get(FINANCIAL_WORK_NAV_PARAM)?.trim() || "";
    const liveFilter = params.get(FINANCIAL_WORK_FILTER_PARAM)?.trim() || "";
    if (liveFw === "receipts" && liveFilter === "billlin_holding") return;
    params.set(FINANCIAL_WORK_NAV_PARAM, "receipts");
    params.set(FINANCIAL_WORK_FILTER_PARAM, "billlin_holding");
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname || "/", { scroll: false });
  }, [pathname, router, searchParams, shareConfirmDeepLink]);

  // LINE [ตรวจรายการ] → ใบเสร็จ / ต้องตรวจ. Drop legacy workerId so 「ข้อมูล」
  // cannot steal the deep link away from the case review modal.
  useEffect(() => {
    if (!caseReviewDeepLink || !caseReviewCaseId) return;
    const params = new URLSearchParams(
      typeof window !== "undefined"
        ? window.location.search
        : searchParams?.toString() || "",
    );
    const liveFw = params.get(FINANCIAL_WORK_NAV_PARAM)?.trim() || "";
    const liveFilter = params.get(FINANCIAL_WORK_FILTER_PARAM)?.trim() || "";
    const liveObject = params.get("object")?.trim() || "";
    const hasWorker = Boolean(params.get("workerId")?.trim());
    if (
      liveFw === "receipts" &&
      liveFilter === "needs_review" &&
      liveObject === "expense_case_id" &&
      !hasWorker
    ) {
      return;
    }
    params.set(FINANCIAL_WORK_NAV_PARAM, "receipts");
    params.set(FINANCIAL_WORK_FILTER_PARAM, "needs_review");
    params.set("object", "expense_case_id");
    params.set("expense_case_id", caseReviewCaseId);
    params.set("rev", "rev_id");
    params.delete("workerId");
    params.delete("filter_sort");
    params.delete("filter_dir");
    params.delete("rev_id");
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname || "/", { scroll: false });
  }, [
    caseReviewCaseId,
    caseReviewDeepLink,
    pathname,
    router,
    searchParams,
  ]);

  // Keep share-drawer skeleton until ModalShell portals, then lift preparing cover.
  useEffect(() => {
    if (!shareConfirmModalOpen) {
      setSharePortalReady(false);
      return;
    }
    const id = window.requestAnimationFrame(() => {
      setSharePortalReady(true);
      preparing?.setReady(true);
      preparing?.setOverviewReady(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, [preparing, shareConfirmModalOpen]);

  // Case review deep link: lift preparing cover so ต้องตรวจ + modal can paint.
  useEffect(() => {
    if (!caseReviewDeepLink || !caseReviewCaseId) return;
    preparing?.setReady(true);
    preparing?.setOverviewReady(true);
  }, [caseReviewCaseId, caseReviewDeepLink, preparing]);

  // Recipient shared-case entry: no workspace shell — lift cover immediately.
  useEffect(() => {
    if (!sharedCaseEntry || !sharedCaseToken) return;
    preparing?.setReady(true);
    preparing?.setOverviewReady(true);
  }, [preparing, sharedCaseEntry, sharedCaseToken]);

  const clearShareConfirmDeepLink = useCallback(
    (opts?: { afterShare?: boolean }) => {
      const params = new URLSearchParams(
        typeof window !== "undefined"
          ? window.location.search
          : searchParams?.toString() || "",
      );
      params.delete(EXPENSE_CONTEXT_IDS_PARAM);
      if (params.get("object")?.trim() === EXPENSE_CONTEXT_IDS_PARAM) {
        params.delete("object");
      }
      params.set(FINANCIAL_WORK_NAV_PARAM, "receipts");
      // Shared receipts leave Bill-Lin กำลังถือ for ต้องตรวจ — land there after commit.
      params.set(
        FINANCIAL_WORK_FILTER_PARAM,
        opts?.afterShare ? "needs_review" : "billlin_holding",
      );
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname || "/", { scroll: false });
    },
    [pathname, router, searchParams],
  );

  useEffect(() => {
    if (!shareConfirmModalOpen || !effectiveWorkspaceId) {
      setShareDeepLinkCards([]);
      return;
    }
    let cancelled = false;
    void Promise.all(
      shareConfirmCaptureIds.map((id) =>
        fetchReceiptCard({
          workspaceId: effectiveWorkspaceId,
          objectRef: { kind: RECEIPT_OBJECT_KIND, id },
        }).catch(() => null),
      ),
    ).then((rows) => {
      if (cancelled) return;
      setShareDeepLinkCards(
        rows.filter((row): row is ReceiptQueueCard => Boolean(row)),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveWorkspaceId, shareConfirmCaptureIds, shareConfirmModalOpen]);

  const shareDeepLinkMeta = useMemo(() => {
    const entries = shareDeepLinkCards
      .map((card) => {
        const id = (card.objectRef?.id || "").trim();
        if (!id) return null;
        return [
          id,
          {
            imageUrl: card.imageUrl,
            sourceLabel: card.sourceLabel,
            contextRows: card.contextRows,
            timelineEvents: card.timelineEvents,
            timelineStatus: card.timelineStatus,
            occurredAt: card.occurredAt,
            updatedAt: card.updatedAt,
            receivedAt: card.receivedAt,
            title: card.title,
            submitterUserId: card.submitterUserId,
          },
        ] as const;
      })
      .filter((row): row is NonNullable<typeof row> => Boolean(row));
    return Object.fromEntries(entries);
  }, [shareDeepLinkCards]);

  const shareDeepLinkEvidence = useMemo(() => {
    const entries = shareDeepLinkCards
      .map((card) => {
        const id = (card.objectRef?.id || "").trim();
        const url = (card.imageUrl || "").trim();
        return id && url ? ([id, url] as const) : null;
      })
      .filter((row): row is readonly [string, string] => Boolean(row));
    return Object.fromEntries(entries);
  }, [shareDeepLinkCards]);

  const unwrapServiceResponse = (response: unknown): Service | null => {
    if (!response || typeof response !== "object") return null;
    if ("service" in response)
      return (response as { service?: Service }).service ?? null;
    if ("data" in response)
      return (response as { data?: Service }).data ?? null;
    return response as Service;
  };

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = (await integrationsApi.getGoogleStatus()) as Record<
        string,
        unknown
      >;
      const parsed = parseGoogleIntegrationStatus(res);
      setStatus(parsed);
      const presentation = getGoogleConnectionPresentation(
        parsed,
        backWorkspaceId,
      );
      setNeedsReconnect(presentation.tone === "needs-reconnect");
    } catch {
      setStatus({ connected: null, sheetsByWorkspace: {} });
      setNeedsReconnect(false);
    } finally {
      setLoadingStatus(false);
    }
  }, [backWorkspaceId]);

  const fetchSheetSyncHealth = useCallback(async () => {
    if (!backWorkspaceId) {
      setSheetSyncHealth(null);
      return;
    }
    try {
      const res = (await transactionsSyncApi.liveMirrorStatus(
        backWorkspaceId,
      )) as { sheet_sync?: string | null };
      const raw = String(res?.sheet_sync ?? "").trim();
      if (
        raw === "failed" ||
        raw === "synced" ||
        raw === "pending" ||
        raw === "needs_reconnect" ||
        raw === "needs_provision"
      ) {
        setSheetSyncHealth(raw);
      } else {
        setSheetSyncHealth(null);
      }
    } catch {
      // Keep last known health — badge falls back to connection-only label.
    }
  }, [backWorkspaceId]);

  const handleProvision = useCallback(async () => {
    if (provisioning) return;
    setProvisioning(true);
    setConnectError(null);
    try {
      await integrationsApi.provisionGoogle(
        backWorkspaceId ? [backWorkspaceId] : undefined,
      );
      await fetchStatus();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/unauthorized_client|invalid_grant|reconnect/i.test(msg)) {
        setNeedsReconnect(true);
        setConnectError(
          "สิทธิ์ Google ใช้ไม่ได้แล้ว กรุณาเชื่อมต่อสำเนาใหม่",
        );
      } else {
        setConnectError(
          "สร้างชีตไม่สำเร็จ — ลองใหม่ หรือเชื่อมต่อ Google อีกครั้ง",
        );
      }
    } finally {
      setProvisioning(false);
    }
  }, [backWorkspaceId, fetchStatus, provisioning]);

  const handleBackfillSheet = useCallback(async () => {
    if (backfilling || !backWorkspaceId) return;
    setBackfilling(true);
    try {
      const res = (await transactionsSyncApi.backfillSheet(backWorkspaceId, {
        forceFull: true,
      })) as {
        status?: string;
        error?: string;
        detail?: { status?: string; error?: string; google_skipped?: boolean; reason?: string };
      };
      const detail = res?.detail;
      const err =
        (typeof res?.error === "string" && res.error) ||
        (typeof detail?.error === "string" && detail.error) ||
        "";
      const needsAuth =
        /unauthorized_client|invalid_grant|reconnect/i.test(err) ||
        detail?.reason === "needs_reconnect";
      if (needsAuth) {
        // Badge/reconnect CTA is enough — no banner for silent open-sheet sync.
        setNeedsReconnect(true);
        await fetchSheetSyncHealth();
        return;
      }
      if (err || res?.status === "error" || detail?.status === "error" || detail?.google_skipped) {
        await fetchSheetSyncHealth();
        return;
      }
      await fetchStatus();
      await fetchSheetSyncHealth();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/unauthorized_client|invalid_grant|reconnect/i.test(msg)) {
        setNeedsReconnect(true);
      }
      await fetchSheetSyncHealth();
    } finally {
      setBackfilling(false);
    }
  }, [backWorkspaceId, backfilling, fetchStatus, fetchSheetSyncHealth]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    void fetchSheetSyncHealth();
  }, [fetchSheetSyncHealth]);

  // Refresh badge when returning to the tab (mirror may have failed in background).
  useEffect(() => {
    const onFocus = () => {
      void fetchSheetSyncHealth();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchSheetSyncHealth]);

  // Auto-provision once when grant is OK but sheet/folder is missing.
  useEffect(() => {
    if (loadingStatus || provisioning || !status) return;
    if (autoProvisionAttemptedRef.current) return;
    const presentation = getGoogleConnectionPresentation(
      status,
      backWorkspaceId,
    );
    if (presentation.tone !== "pending-link") return;
    autoProvisionAttemptedRef.current = true;
    void handleProvision();
  }, [
    loadingStatus,
    provisioning,
    status,
    backWorkspaceId,
    handleProvision,
  ]);

  useEffect(() => {
    setIsDirectPage(/^\/service\/[^/]+/.test(pathname ?? ""));
  }, [pathname]);

  // Surface OAuth failures forwarded from market-api's callback, then clean
  // the URL so a refresh doesn't re-show a stale error.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const err = params.get("googleConnectError");
    if (!err) return;
    setConnectError(err);
    params.delete("googleConnectError");
    const nextQuery = params.toString();
    router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname, {
      scroll: false,
    });
  }, [pathname, router]);

  const clearScrollToParam = useCallback(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const scrollTo = params.get("scrollTo") as BillinScrollTarget | null;
    if (
      scrollTo !== BILLIN_SCROLL_TO_TRANSACTIONS &&
      scrollTo !== BILLIN_SCROLL_TO_RECEIVABLE &&
      scrollTo !== BILLIN_SCROLL_TO_GOOGLE_SHEETS
    ) {
      return;
    }

    params.delete("scrollTo");
    const nextQuery = params.toString();
    router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname, {
      scroll: false,
    });
  }, [pathname, router]);

  const setTransactionsSectionNode = useCallback(
    (node: HTMLDivElement | null) => {
      transactionsSectionRef.current = node;
      setTransactionsSectionEl(node);
    },
    [],
  );

  const setGoogleSheetsSectionNode = useCallback(
    (node: HTMLDivElement | null) => {
      setGoogleSheetsSectionEl(node);
    },
    [],
  );

  const scrollToTarget = searchParams?.get(
    "scrollTo",
  ) as BillinScrollTarget | null;

  const scrollTargetEl =
    scrollToTarget === BILLIN_SCROLL_TO_TRANSACTIONS ||
    scrollToTarget === BILLIN_SCROLL_TO_RECEIVABLE
      ? transactionsSectionEl
      : scrollToTarget === BILLIN_SCROLL_TO_GOOGLE_SHEETS
        ? googleSheetsSectionEl
        : null;

  const scrollTargetReady =
    scrollToTarget === BILLIN_SCROLL_TO_TRANSACTIONS ||
    scrollToTarget === BILLIN_SCROLL_TO_RECEIVABLE
      ? Boolean(backWorkspaceId && transactionsSectionEl)
      : scrollToTarget === BILLIN_SCROLL_TO_GOOGLE_SHEETS
        ? Boolean(googleSheetsSectionEl)
        : false;

  const transactionsInitialWorkerId =
    scrollToTarget === BILLIN_SCROLL_TO_RECEIVABLE
      ? BILLIN_RECEIVABLE_WORKER_ID
      : searchParams?.get("workerId");

  useEffect(() => {
    if (!scrollTargetReady || !scrollTargetEl) {
      return;
    }

    let cancelled = false;
    let settleTimer = 0;
    let finishTimer = 0;
    let fallbackTimer = 0;
    let correctionTimer = 0;
    let kickoffTimer = 0;
    let didScroll = false;

    const finish = () => {
      if (cancelled) return;
      clearScrollToParam();
    };

    const isSectionAligned = () => {
      const rect = scrollTargetEl.getBoundingClientRect();
      const scrollParent = scrollTargetEl.closest(
        ".overflow-y-auto",
      ) as HTMLElement | null;
      const offset = scrollParent
        ? rect.top - scrollParent.getBoundingClientRect().top
        : rect.top;
      return offset >= -8 && offset <= 56;
    };

    const smoothScrollToSection = () => {
      scrollTargetEl.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const scrollOnceWhenSettled = () => {
      if (cancelled || didScroll) return;
      didScroll = true;
      resizeObserver.disconnect();

      smoothScrollToSection();

      correctionTimer = window.setTimeout(() => {
        if (cancelled) return;
        if (!isSectionAligned()) {
          smoothScrollToSection();
        }
        finishTimer = window.setTimeout(finish, 520);
      }, 720);
    };

    const queueSettledScroll = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(scrollOnceWhenSettled, 400);
    };

    const resizeObserver = new ResizeObserver(() => {
      if (!cancelled && !didScroll) {
        queueSettledScroll();
      }
    });

    resizeObserver.observe(scrollTargetEl);
    const layoutRoot = scrollTargetEl.parentElement;
    if (layoutRoot) {
      resizeObserver.observe(layoutRoot);
    }

    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    kickoffTimer = window.setTimeout(queueSettledScroll, isMobile ? 340 : 0);

    fallbackTimer = window.setTimeout(
      () => {
        if (!cancelled && !didScroll) {
          scrollOnceWhenSettled();
        }
      },
      isMobile ? 3000 : 2400,
    );

    return () => {
      cancelled = true;
      window.clearTimeout(settleTimer);
      window.clearTimeout(finishTimer);
      window.clearTimeout(fallbackTimer);
      window.clearTimeout(correctionTimer);
      window.clearTimeout(kickoffTimer);
      resizeObserver.disconnect();
    };
  }, [clearScrollToParam, scrollTargetEl, scrollTargetReady, scrollToTarget]);

  useEffect(() => {
    let cancelled = false;

    const liveWorkspaceId = readLiveWorkspaceId();
    const pickLock = workspacePickLockRef.current;
    const pickIsFresh = !!pickLock && Date.now() - pickLock.at < 2500;
    const fromQuery =
      pickLock && pickIsFresh && liveWorkspaceId !== pickLock.id
        ? pickLock.id
        : liveWorkspaceId || searchParams?.get("workspaceId")?.trim() || null;

    if (fromQuery) {
      queryWorkspaceSeenRef.current = fromQuery;
      setBackWorkspaceId(fromQuery);
    }

    (async () => {
      try {
        const res = await servicesApi.getById(serviceId);
        const service = unwrapServiceResponse(res);
        if (cancelled || !service) return;

        if (service.title?.trim()) {
          setServiceTitle(service.title.trim());
        }

        // LINE [จัดการ N สลิป] opens /?workspaceId= (param present, value empty).
        // The Bill-Lin service creator/user id is not the viewer's shop — using
        // it made the queue show ไม่มีสิทธิ์. Home landing fills the real shop.
        // A missing param (direct /service/… with no workspaceId) still falls
        // through so that surface can resolve a shop from the service record.
        const workspaceParam = searchParams?.get("workspaceId");
        const workspaceParamBlank =
          workspaceParam !== null && workspaceParam.trim() === "";
        if (fromQuery || pickIsFresh || workspaceParamBlank) return;

        const candidate =
          (service as unknown as Record<string, unknown>)["workspace_id"] ??
          service.creatorId ??
          service.serviceCreatorId ??
          service.userId ??
          (service as unknown as Record<string, unknown>)["workspaceId"];

        if (
          !cancelled &&
          typeof candidate === "string" &&
          candidate.trim().length > 0
        ) {
          setBackWorkspaceId(candidate);
        }
      } catch {
        // ignore; button will fall back to home
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [serviceId, searchParams]);

  useEffect(() => {
    const wid = (effectiveWorkspaceId || "").trim();
    if (!wid) {
      setWorkspaceName(null);
      return;
    }

    const fromList = workspaces
      .find((workspace) => workspace.id === wid)
      ?.name?.trim();
    if (fromList) {
      setWorkspaceName(fromList);
      return;
    }
    if (isLoadingWorkspaces) return;

    let cancelled = false;

    (async () => {
      try {
        const raw = await fetchWorkspaceByIdCached(wid);
        const ws = unwrapWorkspace(raw);
        if (!cancelled && ws?.name?.trim()) {
          setWorkspaceName(ws.name.trim());
        }
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveWorkspaceId, isLoadingWorkspaces, workspaces]);
  const googleConnected =
    status?.connected === true ||
    Boolean(getGoogleSheetUrl(status, backWorkspaceId));

  const handleConnect = () => {
    if (typeof window === "undefined") return;
    setConnecting(true);
    setConnectError(null);
    setNeedsReconnect(false);
    autoProvisionAttemptedRef.current = false;
    // Hard navigation for OAuth — soft router.push can leave this page mounted
    // with connecting=true stuck while /login/external hangs on upstream.
    navigateToGoogleIntegrationsConnect({
      serviceId,
      callbackUrl: `${window.location.pathname}${window.location.search}`,
      auto: true,
    });
  };

  useEffect(() => {
    const resetConnecting = () => setConnecting(false);
    // Clear stuck "กำลังเชื่อมต่อ..." when returning via back/bfcache.
    window.addEventListener("pageshow", resetConnecting);
    return () => {
      window.removeEventListener("pageshow", resetConnecting);
    };
  }, []);

  useEffect(() => {
    const container = contentScrollRef.current;
    if (!container) return;

    const syncScrollShadow = () => {
      setIsContentScrolled(container.scrollTop > 0);
    };

    syncScrollShadow();
    container.addEventListener("scroll", syncScrollShadow, { passive: true });

    return () => {
      container.removeEventListener("scroll", syncScrollShadow);
    };
  }, []);

  const activeWorkspaceIndex = workspaces.findIndex(
    (workspace) => workspace.id === effectiveWorkspaceId,
  );
  const hasActiveWorkspace = activeWorkspaceIndex >= 0;

  // R2-02/11 external recipient: opaque token entry — no workspace primary nav / queues.
  if (sharedCaseEntry && sharedCaseToken) {
    return (
      <div
        data-testid="fw-shared-case-recipient-root"
        className="flex h-full min-w-0 flex-col overflow-hidden bg-aera-offwhite min-[1440px]:rounded-tl-3xl min-[1440px]:border-l min-[1440px]:border-t min-[1440px]:border-aera-lightgray min-[1440px]:bg-aera-white"
      >
        <SharedCaseRecipientView
          token={sharedCaseToken}
          linkRevision={sharedCaseLinkRevision}
        />
      </div>
    );
  }

  return (
    <div
      className="h-full min-w-0 flex flex-col overflow-hidden bg-aera-offwhite min-[1440px]:rounded-tl-3xl min-[1440px]:border-l min-[1440px]:border-t min-[1440px]:border-aera-lightgray min-[1440px]:bg-aera-white"
    >
      {!isDesktop ? (
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-aera-lightgray bg-aera-white px-3">
          <Logo
            width={28}
            height={28}
            classNameCrt="#000"
            classNameStr="#F59E0B"
          />
          <div className="h-6 w-px shrink-0 bg-aera-lightgray" aria-hidden />
          <div className="flex h-full min-w-0 flex-1 items-center self-stretch">
            <div className="flex h-full w-max max-w-full min-w-0 items-center gap-2">
              {isLoadingWorkspaces || isLoadingSession ? (
                <div
                  className="flex h-full items-center gap-2"
                  aria-busy="true"
                  aria-label="กำลังโหลดร้าน"
                >
                  {Array.from({ length: 3 }, (_, i) => (
                    <div
                      key={i}
                      className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-aera-lightgray"
                    />
                  ))}
                </div>
              ) : (
              <div
                className="relative flex h-full min-w-0 items-center justify-start gap-2 self-stretch overflow-x-auto scrollbar-hide"
                style={
                  hasActiveWorkspace
                    ? ({ "--ws-active": activeWorkspaceIndex } as CSSProperties)
                    : undefined
                }
              >
                {hasActiveWorkspace ? (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-0 z-20"
                  >
                    <div
                      className={`${WORKSPACE_TOP_INDICATOR_CLASS} transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none`}
                      style={{
                        transform: `translateX(calc(var(--ws-active) * (${WORKSPACE_ITEM_SIZE_PX}px + ${WORKSPACE_ITEM_GAP_PX}px)))`,
                      }}
                    />
                  </div>
                ) : null}
                {workspaces.map((workspace) => {
                  const isActive = workspace.id === effectiveWorkspaceId;
                  return (
                    <button
                      key={workspace.id}
                      type="button"
                      title={workspace.name}
                      onClick={() => {
                        if (workspace.id !== effectiveWorkspaceId) {
                          showWorkspaceSwitchToast(workspace.name);
                        }
                        emitWorkspaceSelected(workspace.id);
                        if (
                          continuePendingBillinGo(workspace.id, (href) => {
                            router.replace(href, { scroll: false });
                          })
                        ) {
                          return;
                        }
                        setBackWorkspaceId(workspace.id);
                        navigateHomeAfterWorkspacePick(
                          (href) => router.replace(href, { scroll: false }),
                          workspace.id,
                          serviceId,
                          orders,
                        );
                      }}
                      className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                        isActive ? "bg-aera-charcoal" : "bg-aera-lightgray"
                      }`}
                    >
                      <WorkspaceSidebarIcon
                        workspace={workspace}
                        active={isActive}
                      />
                    </button>
                  );
                })}
              </div>
              )}
              <AeraButton
                variant="lightgray"
                paddingSize="none"
                onClick={() => setIsWorkspaceModalOpen(true)}
                className="relative z-10 h-10 w-10 min-h-10 min-w-10 shrink-0"
                title="เพิ่มร้าน"
                aria-label="เพิ่มร้าน"
              >
                <Plus
                  className="text-aera-charcoal"
                  size={20}
                  strokeWidth={2.5}
                />
              </AeraButton>
            </div>
          </div>
          <div className="relative z-20 h-10 w-10 shrink-0">
            {isLoginButtonLoading ? (
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full border border-aera-lightgray/80 bg-aera-lightgray"
                aria-busy="true"
                role="status"
              >
                <span className="sr-only">Loading session</span>
              </div>
            ) : isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <AeraButton
                    variant="outline"
                    paddingSize="none"
                    className="h-10 w-10 overflow-hidden rounded-full!"
                    aria-label="เมนูโปรไฟล์"
                  >
                    <AeraImage
                      native
                      src={userAvatar}
                      alt="Profile"
                      referrerPolicy="no-referrer"
                      width={40}
                      height={40}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://ui-avatars.com/api/?name=User&background=random";
                      }}
                    />
                  </AeraButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={8}
                  className="w-auto rounded-lg border border-aera-lightgray/80 bg-aera-white py-2 shadow-xl"
                >
                  {userDisplayName ? (
                    <>
                      <div className="px-4 pb-1 pt-2">
                        <div className="text-xs text-aera-slate/70">
                          {userDisplayName}
                        </div>
                      </div>
                      <div className="my-1 h-px bg-aera-lightgray/80" />
                    </>
                  ) : null}
                  <DropdownMenuItem
                    onClick={() => router.push("/profile")}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-aera-slate transition-colors hover:bg-aera-offwhite"
                  >
                    <User className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <div className="text-nowrap">ดูโปรไฟล์</div>
                  </DropdownMenuItem>
                  <div className="my-1 h-px bg-aera-lightgray/80" />
                  <DropdownMenuItem
                    onClick={() => router.push("/credits/topup")}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-aera-slate transition-colors hover:bg-aera-offwhite"
                  >
                    <Coins className="h-4 w-4 shrink-0 text-aera-amber" strokeWidth={2} />
                    <div className="text-nowrap">เครดิต</div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => router.push("/support")}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-aera-slate transition-colors hover:bg-aera-offwhite"
                  >
                    <Headset className="h-4 w-4 shrink-0 text-aera-amber" strokeWidth={2} />
                    <div className="text-nowrap">ช่วยเหลือ</div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setHowItWorksOpen(true)}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-aera-slate transition-colors hover:bg-aera-offwhite"
                  >
                    <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <div className="text-nowrap">วิธีการทำงาน</div>
                  </DropdownMenuItem>
                  <div className="my-1 h-px bg-aera-lightgray/80" />
                  <DropdownMenuItem
                    onClick={() =>
                      router.push("https://hireaera.com/terms-of-use")
                    }
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-aera-slate transition-colors hover:bg-aera-offwhite"
                  >
                    <div className="text-nowrap">ข้อตกลงใช้งาน</div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => router.push("https://hireaera.com/privacy")}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-aera-slate transition-colors hover:bg-aera-offwhite"
                  >
                    <div className="text-nowrap">นโยบายความเป็นส่วนตัว</div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => openCookieConsentBanner()}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-aera-slate transition-colors hover:bg-aera-offwhite"
                  >
                    <div className="text-nowrap">จัดการคุกกี้</div>
                  </DropdownMenuItem>
                  <div className="my-1 h-px bg-aera-lightgray/80" />
                  <DropdownMenuItem
                    onClick={handleLogoutAction}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-aera-slate transition-colors hover:bg-aera-offwhite"
                  >
                    <LogOut className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <div className="text-nowrap">ออกจากระบบ</div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <AeraButton
                variant="secondary"
                type="button"
                paddingSize="none"
                disabled={isLoginButtonDisabled}
                aria-disabled={isLoginButtonDisabled}
                aria-label="เข้าสู่ระบบ"
                onClick={() => {
                  if (isLoginButtonDisabled) return;
                  setIsLoginNavigationPending(true);
                  navigateToSignin(router, "/signin");
                }}
                className="h-10 w-10 rounded-full!"
              >
                <LogIn
                  size={14}
                  className="h-7 w-7 p-0.75 ps-0"
                  strokeWidth={2.5}
                />
              </AeraButton>
            )}
          </div>
        </div>
      ) : null}

      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <FinancialWorkPreparingCover />
        {shareConfirmDeepLink && !sharePortalReady ? (
          <ShareConfirmPreparingCover fixed />
        ) : null}
        {shareConfirmModalOpen && effectiveWorkspaceId ? (
          <ShareDisclosureConfirmModal
            key={`share-deeplink:${effectiveWorkspaceId}:${shareConfirmCaptureIds.join(",")}`}
            open
            workspaceId={effectiveWorkspaceId}
            captureIds={shareConfirmCaptureIds}
            evidenceByCaptureId={shareDeepLinkEvidence}
            cardsByCaptureId={shareDeepLinkMeta}
            onClose={() => clearShareConfirmDeepLink()}
            onShared={() =>
              clearShareConfirmDeepLink({ afterShare: true })
            }
          />
        ) : null}
        <div
          ref={contentScrollRef}
          className={
            workChrome
              ? "min-h-0 flex-1 overflow-y-auto overscroll-y-contain min-[1440px]:overflow-hidden"
              : "flex min-h-0 flex-1 flex-col overflow-hidden"
          }
        >
        {effectiveWorkspaceId ? (
          <div
            ref={setTransactionsSectionNode}
            id="billin-transactions"
            data-testid="fw-shell-root"
            className={
              workChrome
                ? "flex min-h-full min-w-0 flex-col bg-aera-offwhite max-[899px]:h-auto min-[1440px]:h-full min-[1440px]:min-h-0 min-[1440px]:overflow-hidden min-[1440px]:rounded-tl-3xl min-[1440px]:bg-aera-white"
                : "flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-aera-offwhite min-[1440px]:rounded-tl-3xl min-[1440px]:bg-aera-white"
            }
          >
            <FinancialWorkShell
              workspaceId={effectiveWorkspaceId}
              workspaceName={displayWorkspaceName}
              workspace={
                workspaces.find(
                  (item) => item.id === effectiveWorkspaceId,
                ) ?? null
              }
              workspaces={workspaces}
              isLoadingWorkspaces={isLoadingWorkspaces}
              serviceId={serviceId}
              hired={hired}
              onWorkspaceIdChange={setBackWorkspaceId}
              hireView={<FirstResponder serviceId={serviceId} compactHeader />}
              dataView={
                <div
                  data-testid="fw-data-view"
                  className="flex min-h-full min-w-0 flex-1 flex-col max-[899px]:h-auto max-[899px]:min-h-0 min-[1440px]:min-h-0 min-[1440px]:h-full min-[1440px]:overflow-hidden"
                >
                  <BillinDataView
                    workspaceId={effectiveWorkspaceId}
                    serviceId={serviceId}
                    googleConnected={googleConnected}
                    mode="embedded"
                    containerWidth={containerWidth}
                    workspace={
                      workspaces.find(
                        (workspace) => workspace.id === effectiveWorkspaceId,
                      ) ?? null
                    }
                    initialWorkerId={transactionsInitialWorkerId}
                  />
                </div>
              }
            />
          </div>
        ) : (
          <div className="rounded-2xl border border-aera-lightgray/80 bg-aera-white px-5 py-4 text-sm text-aera-gray">
            กำลังเตรียมข้อมูล workspace...
          </div>
        )}
        </div>
      </div>

      <WorkspaceListModal
        isOpen={isWorkspaceModalOpen}
        mode="create"
        onClose={() => setIsWorkspaceModalOpen(false)}
        onSuccess={(workspaceId) => {
          void fetchWorkspaces();
          setIsWorkspaceModalOpen(false);
          if (!workspaceId) return;
          // A shop created a moment ago cannot be hired — drop the old shop's work params.
          invalidateAccessibleWorkspaces();
          emitWorkspaceSelected(workspaceId);
          setBackWorkspaceId(workspaceId);
          router.replace(
            buildHomeHrefAfterWorkspacePick(workspaceId, BILLIN_SERVICE_ID, {
              isHired: false,
            }),
            { scroll: false },
          );
        }}
      />

      <Modal
        isOpen={howItWorksOpen}
        onClose={() => setHowItWorksOpen(false)}
        title="วิธีการทำงาน"
        size="3xl"
      >
        <div className="space-y-6">
          <HowAeraWorksSection
            className="w-full grid gap-4"
            showTitleIcon={false}
            showCta={false}
          />
        </div>
      </Modal>
    </div>
  );
}
